import { database, ref, get, set } from '../firebase';

const normalize = (value) => String(value || '').trim();

const sanitize = (value) =>
  normalize(value).replace(/[^a-zA-Z0-9_-]/g, '_');

const getDateKey = () => new Date().toISOString().split('T')[0];

const getTimestamp = (input) => {
  const parsed = new Date(input || '').getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildScratchId = (eventType, referenceId) => {
  const safeType = sanitize(eventType || 'event');
  const safeRef = sanitize(referenceId || Date.now());
  return `SCRATCH_${safeType}_${safeRef}`;
};

const DEFAULT_SCRATCH_MIN_REWARDS = {
  fund_add: 1,
  package_activation: 2
};

export const saveScratchHistory = async (payload = {}) => {
  const amount = Number(payload.amount);
  if (!amount || amount <= 0) {
    return { success: false, error: 'Invalid scratch amount' };
  }

  const userId = normalize(payload.userId);
  const memberId = normalize(payload.memberId || payload.userDbKey);
  const sponsorId = normalize(payload.sponsorId) || userId || memberId || 'unknown';
  const dateKey = normalize(payload.dateKey) || getDateKey();
  const eventType = normalize(payload.eventType || 'unknown');
  const referenceId = normalize(payload.referenceId || Date.now());
  const scratchId = buildScratchId(eventType, referenceId);

  const scratchRef = ref(database, `scratch/${dateKey}/${sponsorId}/${scratchId}`);
  const existing = await get(scratchRef);
  if (existing.exists()) {
    return {
      success: true,
      alreadySaved: true,
      id: scratchId,
      record: existing.val()
    };
  }

  const record = {
    id: scratchId,
    dateKey,
    userId,
    memberId,
    sponsorId,
    userName: normalize(payload.userName),
    amount,
    source: normalize(payload.source || 'Scratch Card'),
    eventType,
    description: normalize(payload.description || 'Scratch reward'),
    wallet: normalize(payload.wallet || 'Fund Wallet'),
    referenceId,
    minimumReward: Number(payload.minimumReward) || amount,
    status: normalize(payload.status || 'completed'),
    timestamp: Date.now(),
    timestampFormatted: new Date().toISOString()
  };

  await set(scratchRef, record);
  return { success: true, id: scratchId, record };
};

const isMatchingUser = (record = {}, identifiers = new Set()) => {
  if (!identifiers.size) return false;
  const values = [
    record.userId,
    record.memberId,
    record.sponsorId,
    record.userName
  ].map((item) => normalize(item));
  return values.some((value) => identifiers.has(value));
};

export const fetchUserScratchHistory = async ({
  userId = '',
  memberId = '',
  sponsorId = '',
  userName = ''
} = {}) => {
  const ids = [userId, memberId, sponsorId, userName]
    .map((item) => normalize(item))
    .filter(Boolean);
  const identifiers = new Set(ids);
  const snap = await get(ref(database, 'scratch'));
  if (!snap.exists()) return [];

  const data = snap.val();
  const rows = [];

  Object.entries(data).forEach(([dateKey, users]) => {
    if (!users || typeof users !== 'object') return;
    Object.entries(users).forEach(([userKey, records]) => {
      if (!records || typeof records !== 'object') return;
      Object.entries(records).forEach(([id, record]) => {
        const merged = { id, dateKey, userKey, ...(record || {}) };
        if (identifiers.has(normalize(userKey)) || isMatchingUser(merged, identifiers)) {
          rows.push(merged);
        }
      });
    });
  });

  rows.sort((a, b) => getTimestamp(b.timestampFormatted || b.timestamp) - getTimestamp(a.timestampFormatted || a.timestamp));
  return rows;
};

export const fetchAllScratchHistory = async () => {
  const snap = await get(ref(database, 'scratch'));
  if (!snap.exists()) return [];

  const data = snap.val();
  const rows = [];

  Object.entries(data).forEach(([dateKey, users]) => {
    if (!users || typeof users !== 'object') return;
    Object.entries(users).forEach(([userKey, records]) => {
      if (!records || typeof records !== 'object') return;
      Object.entries(records).forEach(([id, record]) => {
        rows.push({ id, dateKey, userKey, ...(record || {}) });
      });
    });
  });

  rows.sort((a, b) => getTimestamp(b.timestampFormatted || b.timestamp) - getTimestamp(a.timestampFormatted || a.timestamp));
  return rows;
};

export const fetchScratchMinRewards = async () => {
  const snap = await get(ref(database, 'scratchSettings/minRewards'));
  if (!snap.exists()) return { ...DEFAULT_SCRATCH_MIN_REWARDS };

  const data = snap.val() || {};
  const fundAdd = Number(data.fund_add);
  const packageActivation = Number(data.package_activation);

  return {
    fund_add: fundAdd > 0 ? fundAdd : DEFAULT_SCRATCH_MIN_REWARDS.fund_add,
    package_activation:
      packageActivation > 0 ? packageActivation : DEFAULT_SCRATCH_MIN_REWARDS.package_activation
  };
};

export const saveScratchMinRewards = async (payload = {}) => {
  const fundAdd = Number(payload.fund_add);
  const packageActivation = Number(payload.package_activation);

  if (!fundAdd || fundAdd <= 0 || !packageActivation || packageActivation <= 0) {
    return { success: false, error: 'Minimum rewards must be greater than 0' };
  }

  const record = {
    fund_add: fundAdd,
    package_activation: packageActivation,
    updatedAt: new Date().toISOString()
  };

  await set(ref(database, 'scratchSettings/minRewards'), record);
  return { success: true, data: record };
};
